import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../api";
import urls from "../../../urls";
import { omitNullValues } from "../../../utils/omitNullValues";
import type { ConfigurationData } from "../ThirdPartySystems.types";

export function useSubMeteringConfigurationMutations(variantId: number) {
  const queryClient = useQueryClient();

  function invalidateConfigurations() {
    return queryClient.invalidateQueries({
      queryKey: ["sub-metering-configurations", variantId]
    });
  }

  const createSubMeteringConfigurationMutation = useMutation({
    mutationFn: (data: ConfigurationData) =>
      createSubMeteringConfiguration(variantId, data),
    onSettled: invalidateConfigurations
  });

  const updateSubMeteringConfigurationMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ConfigurationData }) =>
      updateSubMeteringConfiguration(variantId, id, data),
    onSettled: invalidateConfigurations
  });

  const triggerHistoricalDataImportMutation = useMutation({
    mutationFn: ({
      configurationId,
      startDate,
      endDate
    }: {
      configurationId: number;
      startDate: string;
      endDate: string;
    }) => triggerHistoricalDataImport(configurationId, startDate, endDate)
  });

  return {
    createSubMeteringConfigurationMutation,
    updateSubMeteringConfigurationMutation,
    triggerHistoricalDataImportMutation
  };
}

function createSubMeteringConfiguration(
  variantId: number,
  data: ConfigurationData
) {
  return api.post<ConfigurationData>(
    urls.api.subMeteringConfigurations(variantId),
    omitNullValues({ ...data, variant: variantId })
  );
}

function updateSubMeteringConfiguration(
  variantId: number,
  id: number,
  data: ConfigurationData
) {
  return api.put<ConfigurationData>(
    urls.api.subMeteringConfiguration(id),
    omitNullValues({ ...data, variant: variantId, id })
  );
}

function triggerHistoricalDataImport(
  configurationId: number,
  startDate: string,
  endDate: string
) {
  return api.post(
    urls.api.subMeteringConfigurationHistoricalDataImport(configurationId),
    { end: endDate, start: startDate }
  );
}
